<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTranslation } from 'i18next-vue';
import { DateTime } from 'luxon';
import Modal from '@/client/components/common/modal.vue';
import type { CalendarEvent } from '@/common/model/events';
import type { CategoryEntry } from '@/client/service/feed';

interface Category {
  id: string;
  name: string;
}

const props = defineProps<{
  preSelectedCategories: Category[];
  allLocalCategories: Category[];
  event?: CalendarEvent;
  confirmLabel?: string;
  dialogTitle?: string;
  sourceCategories?: CategoryEntry[];
}>();

const emit = defineEmits<{
  confirm: [categoryIds: string[], sourceCategoriesToAdopt?: CategoryEntry[]];
  cancel: [];
}>();

const { t } = useTranslation('feed');

// Has-categories mode: track which local categories are selected
const selectedIds = ref<string[]>(
  props.preSelectedCategories
    .map(c => c.id)
    .filter(id => props.allLocalCategories.some(cat => cat.id === id)),
);

// No-categories mode: track which source categories the user wants to adopt
const adoptedSourceIds = ref<string[]>([]);

const hasLocalCategories = computed(() => props.allLocalCategories.length > 0);

const computedDialogTitle = computed(() => {
  if (props.dialogTitle) {
    return props.dialogTitle;
  }
  if (!hasLocalCategories.value) {
    return t('categoryMapping.repostDialogTitleSimple');
  }
  return t('categoryMapping.repostDialogTitle');
});

function toggle(id: string) {
  if (selectedIds.value.includes(id)) {
    selectedIds.value = selectedIds.value.filter(i => i !== id);
  }
  else {
    selectedIds.value = [...selectedIds.value, id];
  }
}

function toggleAdopt(id: string) {
  if (adoptedSourceIds.value.includes(id)) {
    adoptedSourceIds.value = adoptedSourceIds.value.filter(i => i !== id);
  }
  else {
    adoptedSourceIds.value = [...adoptedSourceIds.value, id];
  }
}

function handleConfirm() {
  if (!hasLocalCategories.value) {
    const sourceCatsToAdopt = (props.sourceCategories ?? []).filter(
      (cat) => adoptedSourceIds.value.includes(cat.id),
    );
    emit('confirm', [], sourceCatsToAdopt);
  }
  else {
    emit('confirm', [...selectedIds.value]);
  }
}

function handleCancel() {
  emit('cancel');
}

function formatEventDate(event: CalendarEvent): string | null {
  const schedule = event.baseSchedule();
  if (!schedule?.startDate) return null;
  // Shown in the viewer's zone, like the calendar view's instances.
  return schedule.startDate.toLocal().toLocaleString(DateTime.DATETIME_MED);
}

function getEventSource(url: string): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  }
  catch {
    return null;
  }
}
</script>

<template>
  <Modal
    :title="computedDialogTitle"
    @close="handleCancel"
  >
    <div class="repost-categories-modal">
      <!-- Read-only event details section (shown when event prop is provided) -->
      <div
        v-if="event"
        class="event-details"
      >
        <dl class="event-details-list">
          <div
            v-if="event.content('en').name"
            class="detail-row"
          >
            <dt>{{ t('categoryMapping.eventTitle') }}</dt>
            <dd>{{ event.content('en').name }}</dd>
          </div>
          <div
            v-if="formatEventDate(event)"
            class="detail-row"
          >
            <dt>{{ t('categoryMapping.eventDate') }}</dt>
            <dd>{{ formatEventDate(event) }}</dd>
          </div>
          <div
            v-if="event.content('en').description"
            class="detail-row"
          >
            <dt>{{ t('categoryMapping.eventDescription') }}</dt>
            <dd>{{ event.content('en').description }}</dd>
          </div>
          <div
            v-if="event.location?.name"
            class="detail-row"
          >
            <dt>{{ t('categoryMapping.eventLocation') }}</dt>
            <dd>{{ event.location.name }}</dd>
          </div>
          <div
            v-if="getEventSource(event.eventSourceUrl)"
            class="detail-row"
          >
            <dt>{{ t('categoryMapping.eventSource') }}</dt>
            <dd>{{ getEventSource(event.eventSourceUrl) }}</dd>
          </div>
        </dl>
      </div>

      <!-- Has local categories: show local category checkboxes (existing behavior) -->
      <fieldset
        v-if="hasLocalCategories"
        class="category-fieldset"
      >
        <legend>{{ t('categoryMapping.categoriesLabel') }}</legend>
        <ul class="category-list">
          <li
            v-for="cat in allLocalCategories"
            :key="cat.id"
            class="category-item"
          >
            <label class="category-label">
              <input
                type="checkbox"
                :value="cat.id"
                :checked="selectedIds.includes(cat.id)"
                @change="toggle(cat.id)"
              />
              {{ cat.name }}
            </label>
          </li>
        </ul>
      </fieldset>

      <!-- No local categories: show source category adopt toggles -->
      <fieldset
        v-else-if="sourceCategories && sourceCategories.length > 0"
        class="category-fieldset"
      >
        <legend>{{ t('categoryMapping.adoptCategoryLabel') }}</legend>
        <ul class="category-list">
          <li
            v-for="cat in sourceCategories"
            :key="cat.id"
            class="category-item"
          >
            <button
              type="button"
              role="switch"
              class="adopt-toggle"
              :class="{ active: adoptedSourceIds.includes(cat.id) }"
              :aria-checked="String(adoptedSourceIds.includes(cat.id))"
              :aria-label="cat.name"
              @click="toggleAdopt(cat.id)"
            >
              <span class="adopt-toggle-name" aria-hidden="true">{{ cat.name }}</span>
              <span class="adopt-toggle-label" aria-hidden="true">
                {{ adoptedSourceIds.includes(cat.id) ? t('categoryMapping.adoptCategoryActive') : t('categoryMapping.adoptCategory') }}
              </span>
            </button>
          </li>
        </ul>
      </fieldset>

      <div class="modal-actions">
        <button
          type="button"
          class="btn btn--secondary"
          @click="handleCancel"
        >
          {{ t('categoryMapping.cancel') }}
        </button>
        <button
          type="button"
          class="btn btn--primary"
          @click="handleConfirm"
        >
          {{ confirmLabel ?? t('categoryMapping.repostConfirm') }}
        </button>
      </div>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
div.repost-categories-modal {
  display: flex;
  flex-direction: column;
  gap: var(--pav-space-4);
  min-width: 300px;

  @media (min-width: 768px) {
    min-width: 440px;
  }

  div.event-details {
    padding: var(--pav-space-3);
    background: var(--pav-color-surface-secondary);
    border-radius: var(--pav-border-radius-md);
    border: 1px solid var(--pav-color-border-primary);

    dl.event-details-list {
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: var(--pav-space-2);

      div.detail-row {
        display: grid;
        grid-template-columns: 6rem 1fr;
        gap: var(--pav-space-2);
        align-items: baseline;

        dt {
          font-size: 0.8125rem;
          font-weight: var(--pav-font-weight-medium);
          color: var(--pav-color-text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        dd {
          margin: 0;
          font-size: 0.9375rem;
          color: var(--pav-color-text-primary);
          line-height: 1.4;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
        }
      }
    }
  }

  fieldset.category-fieldset {
    border: none;
    padding: 0;
    margin: 0;

    legend {
      font-size: 0.9375rem;
      font-weight: var(--pav-font-weight-medium);
      color: var(--pav-color-text-primary);
      margin-bottom: var(--pav-space-2);
    }
  }

  ul.category-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--pav-space-2);
    max-height: 240px;
    overflow-y: auto;

    li.category-item {
      label.category-label {
        display: flex;
        align-items: center;
        gap: var(--pav-space-2);
        cursor: pointer;
        font-size: 0.9375rem;
        color: var(--pav-color-text-primary);

        input[type='checkbox'] {
          width: 1rem;
          height: 1rem;
          flex-shrink: 0;
          cursor: pointer;
          accent-color: var(--pav-color-interactive-primary);
        }

        &:hover {
          color: var(--pav-color-interactive-primary);
        }
      }

      button.adopt-toggle {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: var(--pav-space-2) var(--pav-space-3);
        border: 1px solid var(--pav-color-border-primary);
        border-radius: var(--pav-border-radius-md);
        background: var(--pav-color-surface-secondary);
        cursor: pointer;
        font-size: 0.9375rem;
        text-align: left;
        transition: background 0.15s ease, border-color 0.15s ease;

        .adopt-toggle-name {
          color: var(--pav-color-text-primary);
          font-weight: var(--pav-font-weight-medium);
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .adopt-toggle-label {
          color: var(--pav-color-text-secondary);
          font-size: 0.8125rem;
          flex-shrink: 0;
          margin-left: var(--pav-space-3);
        }

        &:hover {
          background: var(--pav-color-surface-tertiary);
          border-color: var(--pav-color-interactive-primary);
        }

        &.active {
          border-color: var(--pav-color-interactive-primary);
          background: var(--pav-color-surface-secondary);

          .adopt-toggle-label {
            color: var(--pav-color-interactive-primary);
            font-weight: var(--pav-font-weight-medium);
          }
        }

        &:focus-visible {
          outline: 2px solid var(--pav-color-interactive-primary);
          outline-offset: 2px;
        }
      }
    }
  }

  div.modal-actions {
    display: flex;
    gap: var(--pav-space-3);
    justify-content: flex-end;
    margin-top: var(--pav-space-2);

    button {
      padding: var(--pav-space-2) var(--pav-space-4);
      border-radius: var(--pav-border-radius-pill);
      font-weight: var(--pav-font-weight-medium);
      cursor: pointer;
      transition: all 0.2s ease;

      &.secondary {
        background: transparent;
        border: 1px solid var(--pav-color-border-primary);
        color: var(--pav-color-text-primary);

        &:hover {
          background: var(--pav-color-surface-secondary);
        }
      }

      &.primary {
        background: var(--pav-color-interactive-primary);
        border: none;
        color: var(--pav-color-text-inverse);

        &:hover {
          background: var(--pav-color-interactive-primary-hover);
        }
      }

      &:focus-visible {
        outline: 2px solid var(--pav-color-interactive-primary);
        outline-offset: 2px;
      }
    }
  }
}
</style>
